/**
 * SVG Image Slider
 *
 * Desktop (> 900px) — GSAP ScrollTrigger with pin:
 * - Page pins, scroll drives horizontal image transitions
 * - Background colour interpolates continuously
 * - Snaps to nearest slide on release
 *
 * Mobile (≤ 900px) — touch swipe + prev/next buttons, NO scroll-hijack:
 * - Normal document flow — page scrolls freely past the slider
 * - Prev/Next buttons injected at runtime (cleaned up on resize)
 * - Horizontal swipe (touchstart → touchend) advances slides
 * - GSAP tweens the slide-in for a smooth transition
 * - Keyboard arrows also supported
 *
 * Split handled by gsap.matchMedia() — GSAP's recommended pattern
 * for breakpoint-scoped animations with automatic cleanup on resize.
 *
 * Accessibility:
 * - prefers-reduced-motion → instant switching on both paths
 * - aria-live region announces current slide
 * - Keyboard arrows work on both paths
 */

(function () {

    // ── Colour helpers ────────────────────────────────────────────────

    function lerpColor(c1, c2, t) {
        return c1.map((v, i) => Math.round(v + t * (c2[i] - v)));
    }

    function makeBgAt(colors) {
        return function bgAt(p) {
            const n   = colors.length;
            const pos = p * (n - 1);
            const ci  = Math.min(Math.floor(pos), n - 2);
            const [r, g, b] = lerpColor(colors[ci], colors[ci + 1], pos - ci);
            return `rgb(${r},${g},${b})`;
        };
    }

    // ── Init ──────────────────────────────────────────────────────────
    function init() {
        gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        document.querySelectorAll('.svg-image-slider').forEach(function (slider) {

            const totalSlides = parseInt(slider.dataset.totalSlides, 10);
            if (!totalSlides || totalSlides < 2) return;

            const imageSlides = Array.from(slider.querySelectorAll('.svg-slider__image-slide'));
            const textSlides  = Array.from(slider.querySelectorAll('.svg-slider__text-slide'));
            const liveRegion  = slider.querySelector('.svg-slider__live');
            const segments    = totalSlides - 1;

            if (!slider.id) slider.id = 'svg-slider-carousel';

            const colors = [
                [130, 165, 255],
                [145, 215, 200],
                [210, 220,  75],
            ];
            const bgAt = makeBgAt(colors);

            function announce(index) {
                const label = textSlides[index]?.getAttribute('aria-label') || `Slide ${index + 1} of ${totalSlides}`;
                if (liveRegion) liveRegion.textContent = label;
            }

            // ── REDUCED MOTION ────────────────────────────────────────
            if (reducedMotion) {
                imageSlides.forEach((slide, i) => {
                    slide.style.transition = 'none';
                    slide.style.transform  = i === 0 ? 'translateX(0)' : 'translateX(-100%)';
                    slide.style.opacity    = '1';
                });
                textSlides.forEach((t, i) => {
                    t.classList.toggle('is-active', i === 0);
                    t.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
                    t.style.transition = 'none';
                });
                slider.style.background = bgAt(0);

                let rmIndex = 0;
                function rmGoTo(index) {
                    index = Math.max(0, Math.min(totalSlides - 1, index));
                    if (index === rmIndex) return;
                    imageSlides[rmIndex].style.transform = 'translateX(-100%)';
                    textSlides[rmIndex].classList.remove('is-active');
                    textSlides[rmIndex].setAttribute('aria-hidden', 'true');
                    rmIndex = index;
                    imageSlides[index].style.transform = 'translateX(0)';
                    textSlides[index].classList.add('is-active');
                    textSlides[index].setAttribute('aria-hidden', 'false');
                    slider.style.background = bgAt(index / segments);
                    announce(index);
                }
                slider.parentElement.addEventListener('keydown', e => {
                    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); rmGoTo(rmIndex + 1); }
                    if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   { e.preventDefault(); rmGoTo(rmIndex - 1); }
                });
                announce(0);
                return;
            }

            // ── gsap.matchMedia() ─────────────────────────────────────
            const mm = gsap.matchMedia();

            // ── MOBILE (≤ 900px) — touch swipe + prev/next buttons ───
            /*
             * How it works:
             *
             * 1. Slides snap with a short GSAP tween on:
             *    - Prev / Next button tap
             *    - Horizontal swipe (touchstart → touchend delta)
             *    - Keyboard arrow keys
             *
             * 2. No scroll-space, no sticky — the slider is a normal
             *    block in the document flow. Page scrolls freely.
             *
             * 3. GSAP animates the slide-in so the visual transition
             *    matches the desktop feel.
             */
            mm.add('(max-width: 900px)', () => {

                let currentSlideIndex = 0;

                // ── Inject prev/next buttons ─────────────────────────
                const prevBtn = document.createElement('button');
                prevBtn.className = 'svg-slider__btn svg-slider__btn--prev';
                prevBtn.setAttribute('aria-label', 'Previous slide');
                prevBtn.innerHTML = '<svg width="45" height="41" viewBox="0 0 45 41" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M20.4594 40.4974C21.0931 41.1171 22.2208 41.1486 22.8544 40.5289C23.4881 39.9093 23.4666 38.8066 22.8222 38.187L6.40095 22.1294L44.9893 22.1294L44.9893 18.8527L6.4117 18.8527L22.8329 2.79508C23.4666 2.17546 23.4988 1.07275 22.8652 0.453127C22.2315 -0.166494 21.1038 -0.145491 20.4702 0.484632L2.37351 18.1806L-2.02457e-06 20.5015L20.4594 40.4974Z" fill="#1D1D1B"/></svg>';
                slider.appendChild(prevBtn);

                const nextBtn = document.createElement('button');
                nextBtn.className = 'svg-slider__btn svg-slider__btn--next';
                nextBtn.setAttribute('aria-label', 'Next slide');
                nextBtn.innerHTML = '<svg width="45" height="41" viewBox="0 0 45 41" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M24.5298 0.482966C23.8962 -0.136655 22.7685 -0.168161 22.1348 0.45146C21.5012 1.07108 21.5227 2.1738 22.1671 2.79342L38.5883 18.851H0V22.1277H38.5776L22.1563 38.1853C21.5227 38.8049 21.4905 39.9077 22.1241 40.5273C22.7578 41.1469 23.8854 41.1259 24.5191 40.4958L42.6158 22.7998L44.9893 20.4789L24.5298 0.482966Z" fill="#1D1D1B"/></svg>';
                slider.appendChild(nextBtn);

                // ── Reset all slides to initial state ────────────────
                imageSlides.forEach((slide, i) => {
                    gsap.set(slide, { x: i === 0 ? '0%' : '-100%', willChange: 'transform' });
                });

                let activeText = 0;
                textSlides.forEach((t, i) => {
                    t.classList.toggle('is-active', i === 0);
                    t.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
                });

                slider.style.background = bgAt(0);
                updateButtons();

                function updateButtons() {
                    prevBtn.disabled = currentSlideIndex === 0;
                    nextBtn.disabled = currentSlideIndex === totalSlides - 1;
                }

                function syncText(index) {
                    if (index === activeText) return;
                    textSlides.forEach(t => { t.style.transition = 'none'; });
                    textSlides[activeText].classList.remove('is-active');
                    textSlides[activeText].setAttribute('aria-hidden', 'true');
                    activeText = index;
                    textSlides[index].classList.add('is-active');
                    textSlides[index].setAttribute('aria-hidden', 'false');
                    requestAnimationFrame(() => {
                        textSlides.forEach(t => { t.style.transition = ''; });
                    });
                    announce(index);
                }

                function goTo(index) {
                    index = Math.max(0, Math.min(totalSlides - 1, index));
                    if (index === currentSlideIndex) return;

                    const dir       = index > currentSlideIndex ? 1 : -1;
                    const fromSlide = imageSlides[currentSlideIndex];
                    const toSlide   = imageSlides[index];

                    // Position the incoming slide off-screen in the correct direction
                    gsap.set(toSlide, { x: dir > 0 ? '-100%' : '200%' });

                    const tl = gsap.timeline({ defaults: { duration: 0.45, ease: 'power2.inOut' } });
                    tl.to(fromSlide, { x: dir > 0 ? '200%' : '-100%' }, 0);
                    tl.to(toSlide,   { x: '0%' },                        0);

                    currentSlideIndex = index;
                    slider.style.background = bgAt(index / segments);
                    syncText(index);
                    updateButtons();
                }

                // ── Button listeners ─────────────────────────────────
                prevBtn.addEventListener('click', () => goTo(currentSlideIndex - 1));
                nextBtn.addEventListener('click', () => goTo(currentSlideIndex + 1));

                // ── Touch swipe ──────────────────────────────────────
                let touchStartX = null;
                const SWIPE_THRESHOLD = 40; // px

                slider.addEventListener('touchstart', e => {
                    touchStartX = e.touches[0].clientX;
                }, { passive: true });

                slider.addEventListener('touchend', e => {
                    if (touchStartX === null) return;
                    const delta = e.changedTouches[0].clientX - touchStartX;
                    touchStartX = null;
                    if (Math.abs(delta) < SWIPE_THRESHOLD) return;
                    goTo(currentSlideIndex + (delta < 0 ? 1 : -1));
                }, { passive: true });

                // ── Keyboard ─────────────────────────────────────────
                const keyHandler = e => {
                    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); goTo(currentSlideIndex + 1); }
                    if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   { e.preventDefault(); goTo(currentSlideIndex - 1); }
                };
                slider.parentElement.addEventListener('keydown', keyHandler);

                announce(0);

                // ── Cleanup on resize ─────────────────────────────────
                return () => {
                    prevBtn.remove();
                    nextBtn.remove();
                    slider.parentElement.removeEventListener('keydown', keyHandler);
                    imageSlides.forEach(slide => {
                        gsap.set(slide, { clearProps: 'transform,willChange' });
                    });
                };
            });

            // ── DESKTOP (> 900px) — GSAP pin + scrub ─────────────────
            mm.add('(min-width: 901px)', () => {

                ScrollTrigger.normalizeScroll(true);

                imageSlides.forEach((slide, i) => {
                    slide.style.transition = 'none';
                    slide.style.willChange = 'transform';
                    slide.style.transform  = i === 0 ? 'translateX(0%)' : 'translateX(-100%)';
                    slide.style.opacity    = '1';
                });

                let activeText = 0;
                textSlides.forEach((t, i) => {
                    t.classList.toggle('is-active', i === 0);
                    t.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
                });

                function syncText(index) {
                    if (index === activeText) return;
                    // Kill CSS transition so the swap is instant — hidden behind the image
                    textSlides.forEach(t => { t.style.transition = 'none'; });
                    textSlides[activeText].classList.remove('is-active');
                    textSlides[activeText].setAttribute('aria-hidden', 'true');
                    activeText = index;
                    textSlides[index].classList.add('is-active');
                    textSlides[index].setAttribute('aria-hidden', 'false');
                    // Re-enable transition on next frame (keeps hover/focus states smooth)
                    requestAnimationFrame(() => {
                        textSlides.forEach(t => { t.style.transition = ''; });
                    });
                    announce(index);
                }

                let lastSeg   = -1;
                let lastTextI = 0;

                function applyProgress(globalP) {
                    slider.style.background = bgAt(globalP);

                    const scaled = Math.min(globalP * segments, segments - 0.0001);
                    const seg    = Math.floor(scaled);
                    const localP = scaled - seg;

                    const textTarget = localP >= 0.5 ? seg + 1 : seg;
                    if (textTarget !== lastTextI) {
                        syncText(textTarget);
                        lastTextI = textTarget;
                    }

                    if (seg !== lastSeg) {
                        imageSlides.forEach((slide, i) => {
                            if (i < seg)          slide.style.transform = 'translateX(200%)';
                            else if (i > seg + 1) slide.style.transform = 'translateX(-100%)';
                        });
                        lastSeg = seg;
                    }

                    imageSlides[seg].style.transform     = `translateX(${localP * 200}%)`;
                    imageSlides[seg + 1].style.transform = `translateX(${-100 + localP * 100}%)`;
                }

                let currentSlideIndex = 0;

                // Build snap positions: one on each slide + one pause point at 0.8
                // through each segment — old slide is nearly gone, new one not yet visible.
                const snapPositions = [];
                for (let i = 0; i <= segments; i++) {
                    snapPositions.push(i / segments);
                    if (i < segments) snapPositions.push((i + 0.8) / segments);
                }

                ScrollTrigger.create({
                    trigger      : slider,
                    start        : 'top top',
                    end          : () => `+=${segments * 2500}vh`,
                    pin          : true,
                    anticipatePin: 1,
                    scrub        : 1,
                    snap         : {
                        snapTo  : snapPositions,
                        duration: { min: 0.3, max: 0.8 },
                        ease    : 'power2.inOut',
                        delay   : 0.05,
                    },
                    onUpdate(self) {
                        applyProgress(self.progress);
                        currentSlideIndex = Math.round(self.progress * segments);
                        slider.dispatchEvent(new CustomEvent('slider:progress', {
                            detail: { progress: self.progress },
                            bubbles: false,
                        }));
                    },
                    onLeave() {
                        slider.dispatchEvent(new CustomEvent('slider:leave', { bubbles: false }));
                    },
                    onLeaveBack() {
                        slider.dispatchEvent(new CustomEvent('slider:enter', { bubbles: false }));
                    },
                    onEnter() {
                        slider.dispatchEvent(new CustomEvent('slider:enter', { bubbles: false }));
                    },
                });

                function jumpTo(index) {
                    index = Math.max(0, Math.min(totalSlides - 1, index));
                    const st = ScrollTrigger.getAll().find(t => t.vars.trigger === slider);
                    if (!st) return;
                    const target = st.start + (index / segments) * (st.end - st.start);
                    gsap.to(window, { scrollTo: target, duration: 1.5, ease: 'power2.inOut' });
                }

                slider.parentElement.addEventListener('keydown', e => {
                    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); jumpTo(currentSlideIndex + 1); }
                    if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   { e.preventDefault(); jumpTo(currentSlideIndex - 1); }
                });

                applyProgress(0);
                announce(0);

                return () => {
                    ScrollTrigger.normalizeScroll(false);
                };
            });
        });
    }

    // GSAP and its plugins are enqueued by WordPress (functions.php) before
    // this script loads — no dynamic loading needed.
    document.addEventListener('DOMContentLoaded', init);

})();